/**
 * KeysScreen — key / permission management (updateauth). Lets the active
 * account rotate the key on its owner or active permission.
 *
 * Signing-permission trap (eos-backend-dev skill #2): updating 'owner' must
 * be signed by 'owner' itself; updating 'active' is signed with whatever
 * permission the live session actually holds — never a hardcoded 'active'.
 */

import { useEffect, useState } from 'react'
import type { Session } from '@wharfkit/session'
import { PublicKey } from '@wharfkit/antelope'
import { getAccountPermissions, updateAuthKey, type AccountPermission } from '@properwallet/chain-eos'
import { useWalletStore } from '../store/wallet.store'
import { useTranslation } from '../hooks/useTranslation'

type Perm = 'owner' | 'active'

export default function KeysScreen() {
  const { t } = useTranslation()
  const { activeAccount, activeSession, goBack } = useWalletStore()

  const [perms, setPerms] = useState<AccountPermission[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [target, setTarget] = useState<Perm>('active')
  const [newKey, setNewKey] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const load = () => {
    if (!activeAccount) return
    setLoading(true)
    setLoadError('')
    getAccountPermissions(activeAccount.name)
      .then(setPerms)
      .catch(() => setLoadError(t('keys.loadFailed')))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [activeAccount?.name]) // eslint-disable-line react-hooks/exhaustive-deps

  const current = perms.find(p => p.permission === target)

  const keyValid = (() => {
    try { PublicKey.from(newKey.trim()); return true } catch { return false }
  })()

  const handleUpdate = async () => {
    if (!activeAccount || !activeSession || !keyValid) return
    setError('')
    setSuccess('')
    setBusy(true)
    try {
      const session = activeSession as Session
      // 'owner' can only ever be signed by 'owner'; anything else uses the
      // permission this session is actually logged in with.
      const signingPermission = target === 'owner' ? 'owner' : session.permission.toString()

      await updateAuthKey(session, {
        account: activeAccount.name,
        permission: target,
        newKey: newKey.trim(),
        signingPermission,
      })
      setSuccess(t('keys.success'))
      setNewKey('')
      load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col h-full bg-surface">
      <div className="bg-surface-1 pt-safe px-4 py-3 border-b border-surface-3 flex items-center gap-3 flex-shrink-0">
        <button onClick={goBack} className="text-gray-400 text-sm">← {t('common.back')}</button>
        <h2 className="text-white font-semibold flex-1 text-center">{t('keys.title')}</h2>
        <div className="w-16" />
      </div>

      <div className="scroll-area flex-1 px-4 py-4 space-y-4">
        <p className="text-xs text-gray-400">{t('keys.intro')}</p>

        <div className="card space-y-3">
          <p className="text-sm font-semibold text-white">{t('keys.permission')}</p>
          <div className="flex gap-2">
            <button
              className={`flex-1 py-2 rounded-lg text-xs ${target === 'active' ? 'bg-brand text-white' : 'bg-surface-2 text-gray-400'}`}
              onClick={() => setTarget('active')}
            >
              {t('keys.active')}
            </button>
            <button
              className={`flex-1 py-2 rounded-lg text-xs ${target === 'owner' ? 'bg-brand text-white' : 'bg-surface-2 text-gray-400'}`}
              onClick={() => setTarget('owner')}
            >
              {t('keys.owner')}
            </button>
          </div>

          <div>
            <p className="text-xs text-gray-400 mb-1">{t('keys.currentKey')}</p>
            {loading ? (
              <p className="text-xs text-gray-500">{t('common.loading')}</p>
            ) : loadError ? (
              <p className="text-xs text-danger">{loadError}</p>
            ) : current ? (
              current.keys.map(k => (
                <p key={k.key} className="input-field-mono break-all mb-1" style={{ height: 'auto', paddingTop: 8, paddingBottom: 8 }}>
                  {k.key} <span className="text-gray-500">(w{k.weight})</span>
                </p>
              ))
            ) : (
              <p className="text-xs text-gray-500">—</p>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs text-gray-400">{t('keys.newKey')}</label>
          <input
            className="input-field-mono"
            value={newKey}
            onChange={e => setNewKey(e.target.value.trim())}
            placeholder="EOS... / PUB_K1_..."
          />
          <p className="text-xs text-gray-500">{t('keys.newKeyHint')}</p>
        </div>

        <p className="text-danger text-xs">{t('keys.dangerWarning')}</p>
        {target === 'owner' && <p className="text-danger text-xs">{t('keys.ownerWarning')}</p>}

        {error ? <p className="text-danger text-xs text-center">{error}</p> : null}
        {success ? <p className="text-success text-xs text-center">{success}</p> : null}

        <button
          className="btn-danger w-full"
          disabled={!keyValid || busy || !activeSession}
          onClick={handleUpdate}
        >
          {busy ? t('keys.updating') : t('keys.update')}
        </button>
      </div>
    </div>
  )
}
